import React, { useState, useEffect } from 'react';
import { AlertCircle, TrendingUp, TrendingDown } from 'lucide-react';
import { CurrencyInput } from '../CurrencyInput';
import { parseCurrency } from '../../utils/currency';

interface FinancialInfoData {
  monthlyIncome: string;
  otherIncomeSources?: string;
  monthlyExpenses: string;
  rentMortgagePayment?: string;
  dependents: number;
  existingLoansCount: number;
  existingLoansTotal: string;
}

interface FinancialInfoStepProps {
  initialData?: Partial<FinancialInfoData>;
  employmentIncome?: string; // From previous step
  onNext: (data: FinancialInfoData) => void;
  onBack: () => void;
}

export const FinancialInfoStep: React.FC<FinancialInfoStepProps> = ({
  initialData,
  employmentIncome,
  onNext,
  onBack,
}) => {
  const [formData, setFormData] = useState<FinancialInfoData>({
    monthlyIncome: initialData?.monthlyIncome || employmentIncome || '',
    otherIncomeSources: initialData?.otherIncomeSources || '',
    monthlyExpenses: initialData?.monthlyExpenses || '',
    rentMortgagePayment: initialData?.rentMortgagePayment || '',
    dependents: initialData?.dependents || 0,
    existingLoansCount: initialData?.existingLoansCount || 0,
    existingLoansTotal: initialData?.existingLoansTotal || '0',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof FinancialInfoData, string>>>({});
  const [touchedFields, setTouchedFields] = useState<Set<keyof FinancialInfoData>>(new Set());
  const [debtToIncomeRatio, setDebtToIncomeRatio] = useState<number>(0);

  // Calculate debt-to-income ratio
  useEffect(() => {
    const income = parseFloat(formData.monthlyIncome) || 0;
    const expenses = parseFloat(formData.monthlyExpenses) || 0;
    const existingLoans = parseFloat(formData.existingLoansTotal) || 0;

    if (income > 0) {
      const totalDebt = expenses + existingLoans;
      const ratio = (totalDebt / income) * 100;
      setDebtToIncomeRatio(Math.round(ratio * 100) / 100); // Round to 2 decimal places
    } else {
      setDebtToIncomeRatio(0);
    }
  }, [formData.monthlyIncome, formData.monthlyExpenses, formData.existingLoansTotal]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name as keyof FinancialInfoData]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleBlur = (field: keyof FinancialInfoData) => {
    setTouchedFields((prev) => new Set(prev).add(field));
    validateField(field);
  };

  const validateField = (field: keyof FinancialInfoData): string => {
    const value = formData[field];
    let error = '';

    switch (field) {
      case 'monthlyIncome':
        if (!value || parseFloat(value as string) <= 0) {
          error = 'Monthly income is required and must be greater than 0';
        }
        break;

      case 'monthlyExpenses':
        if (!value || parseFloat(value as string) < 0) {
          error = 'Monthly expenses is required';
        } else {
          const income = parseFloat(formData.monthlyIncome) || 0;
          const expenses = parseFloat(value as string) || 0;
          if (expenses > income) {
            error = 'Monthly expenses cannot exceed monthly income';
          }
        }
        break;

      case 'rentMortgagePayment':
        if (value && parseFloat(value as string) < 0) {
          error = 'Rent/mortgage payment must be 0 or greater';
        }
        break;

      case 'dependents':
        const dependents = Number(value);
        if (isNaN(dependents) || dependents < 0) {
          error = 'Number of dependents must be 0 or greater';
        } else if (dependents > 20) {
          error = 'Please enter a realistic number of dependents';
        }
        break;

      case 'existingLoansCount':
        const loansCount = Number(value);
        if (isNaN(loansCount) || loansCount < 0) {
          error = 'Number of existing loans must be 0 or greater';
        } else if (loansCount > 0 && parseFloat(formData.existingLoansTotal) <= 0) {
          error = 'Please enter the total amount of existing loans';
        }
        break;

      case 'existingLoansTotal':
        const loansTotal = parseFloat(value as string);
        if (formData.existingLoansCount > 0 && (!value || loansTotal <= 0)) {
          error = 'Total amount of existing loans is required if you have existing loans';
        } else if (loansTotal < 0) {
          error = 'Total amount must be 0 or greater';
        }
        break;
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
    return error;
  };

  const validateAll = (): boolean => {
    const fieldsToValidate: (keyof FinancialInfoData)[] = [
      'monthlyIncome',
      'monthlyExpenses',
      'dependents',
      'existingLoansCount',
      'existingLoansTotal',
    ];

    // Add optional fields if they have values
    if (formData.rentMortgagePayment) {
      fieldsToValidate.push('rentMortgagePayment');
    }

    const newErrors: Partial<Record<keyof FinancialInfoData, string>> = {};
    let hasErrors = false;

    fieldsToValidate.forEach((field) => {
      const error = validateField(field);
      if (error) {
        newErrors[field] = error;
        hasErrors = true;
      }
    });

    setErrors(newErrors);
    setTouchedFields(new Set(fieldsToValidate));

    return !hasErrors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateAll()) {
      onNext(formData);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getDTIRating = (ratio: number): { label: string; color: string; icon: JSX.Element } => {
    if (ratio === 0) return { label: 'Not calculated', color: 'gray', icon: <AlertCircle size={20} /> };
    if (ratio < 20) return { label: 'Excellent', color: 'green', icon: <TrendingUp size={20} /> };
    if (ratio < 30) return { label: 'Very Good', color: 'lightgreen', icon: <TrendingUp size={20} /> };
    if (ratio < 40) return { label: 'Good', color: 'yellow', icon: <TrendingUp size={20} /> };
    if (ratio < 50) return { label: 'Fair', color: 'orange', icon: <TrendingDown size={20} /> };
    return { label: 'High Risk', color: 'red', icon: <TrendingDown size={20} /> };
  };

  const dtiRating = getDTIRating(debtToIncomeRatio);
  const monthlyIncome = parseFloat(formData.monthlyIncome) || 0;
  const monthlyExpenses = parseFloat(formData.monthlyExpenses) || 0;
  const existingLoansTotal = parseFloat(formData.existingLoansTotal) || 0;
  const disposableIncome = monthlyIncome - monthlyExpenses - existingLoansTotal;

  return (
    <form onSubmit={handleSubmit} className="application-form">
      <div className="form-section">
        <h3>Financial Information</h3>
        <p className="section-description">
          Please provide accurate information about your income and expenses. This helps us assess your loan eligibility. Fields marked with * are required.
        </p>

        {/* Income Section */}
        <h4 className="subsection-title">Income Details</h4>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="monthlyIncome">Total Monthly Income (₦) *</label>
            <CurrencyInput
              id="monthlyIncome"
              name="monthlyIncome"
              value={formData.monthlyIncome}
              onChange={(value) => {
                setFormData({ ...formData, monthlyIncome: value.toString() });
                if (errors.monthlyIncome) {
                  setErrors({ ...errors, monthlyIncome: '' });
                }
              }}
              onBlur={() => handleBlur('monthlyIncome')}
              required
              min={0}
              placeholder="Enter total monthly income"
              error={touchedFields.has('monthlyIncome') ? errors.monthlyIncome : ''}
              showLabel={false}
            />
            <span className="form-hint">Include salary, business income, and all other regular income</span>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="otherIncomeSources">Other Income Sources (Optional)</label>
          <textarea
            id="otherIncomeSources"
            name="otherIncomeSources"
            value={formData.otherIncomeSources}
            onChange={handleChange}
            rows={2}
            placeholder="e.g., Rental income, dividends, freelance work, etc."
          />
          <span className="form-hint">List any additional sources of income</span>
        </div>

        {/* Expenses Section */}
        <h4 className="subsection-title">Monthly Expenses</h4>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="monthlyExpenses">Total Monthly Expenses (₦) *</label>
            <CurrencyInput
              id="monthlyExpenses"
              name="monthlyExpenses"
              value={formData.monthlyExpenses}
              onChange={(value) => {
                setFormData({ ...formData, monthlyExpenses: value.toString() });
                if (errors.monthlyExpenses) {
                  setErrors({ ...errors, monthlyExpenses: '' });
                }
              }}
              onBlur={() => handleBlur('monthlyExpenses')}
              required
              min={0}
              placeholder="Enter total monthly expenses"
              error={touchedFields.has('monthlyExpenses') ? errors.monthlyExpenses : ''}
              showLabel={false}
            />
            <span className="form-hint">Include food, transport, utilities, and other regular expenses</span>
          </div>

          <div className="form-group">
            <label htmlFor="rentMortgagePayment">Rent / Mortgage Payment (₦)</label>
            <CurrencyInput
              id="rentMortgagePayment"
              name="rentMortgagePayment"
              value={formData.rentMortgagePayment || ''}
              onChange={(value) => {
                setFormData({ ...formData, rentMortgagePayment: value.toString() });
              }}
              min={0}
              placeholder="Enter monthly housing cost"
              showLabel={false}
            />
            <span className="form-hint">Optional - monthly housing cost</span>
          </div>
        </div>

        {/* Dependents */}
        <div className="form-group">
          <label htmlFor="dependents">Number of Dependents *</label>
          <input
            type="number"
            id="dependents"
            name="dependents"
            value={formData.dependents}
            onChange={handleChange}
            onBlur={() => handleBlur('dependents')}
            min="0"
            max="20"
            required
            placeholder="Enter number of dependents"
            className={touchedFields.has('dependents') && errors.dependents ? 'error' : ''}
          />
          {touchedFields.has('dependents') && errors.dependents && (
            <span className="field-error">{errors.dependents}</span>
          )}
          <span className="form-hint">Number of people financially dependent on you</span>
        </div>

        {/* Existing Loans */}
        <h4 className="subsection-title">Existing Loan Obligations</h4>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="existingLoansCount">Number of Existing Loans *</label>
            <input
              type="number"
              id="existingLoansCount"
              name="existingLoansCount"
              value={formData.existingLoansCount}
              onChange={handleChange}
              onBlur={() => handleBlur('existingLoansCount')}
              min="0"
              max="10"
              required
              placeholder="Enter number of existing loans"
              className={touchedFields.has('existingLoansCount') && errors.existingLoansCount ? 'error' : ''}
            />
            {touchedFields.has('existingLoansCount') && errors.existingLoansCount && (
              <span className="field-error">{errors.existingLoansCount}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="existingLoansTotal">
              Total Monthly Loan Payments (₦) {formData.existingLoansCount > 0 && '*'}
            </label>
            <CurrencyInput
              id="existingLoansTotal"
              name="existingLoansTotal"
              value={formData.existingLoansTotal}
              onChange={(value) => {
                setFormData({ ...formData, existingLoansTotal: value.toString() });
              }}
              min={0}
              placeholder="Enter total monthly loan payments"
              showLabel={false}
            />
            <span className="form-hint">
              {formData.existingLoansCount > 0
                ? 'Sum of all monthly payments for existing loans'
                : 'Enter 0 if you have no existing loans'}
            </span>
          </div>
        </div>
      </div>

      {/* Financial Summary & DTI Calculator */}
      <div className="form-section">
        <h3>Financial Summary</h3>

        <div className="financial-summary-grid">
          <div className="summary-card">
            <div className="summary-label">Monthly Income</div>
            <div className="summary-value income">{formatCurrency(monthlyIncome)}</div>
          </div>

          <div className="summary-card">
            <div className="summary-label">Monthly Expenses</div>
            <div className="summary-value expense">({formatCurrency(monthlyExpenses)})</div>
          </div>

          <div className="summary-card">
            <div className="summary-label">Existing Loan Payments</div>
            <div className="summary-value expense">({formatCurrency(existingLoansTotal)})</div>
          </div>

          <div className="summary-card highlight">
            <div className="summary-label">Disposable Income</div>
            <div className={`summary-value ${disposableIncome >= 0 ? 'positive' : 'negative'}`}>
              {formatCurrency(disposableIncome)}
            </div>
          </div>
        </div>

        {/* Debt-to-Income Ratio */}
        <div className={`dti-calculator ${dtiRating.color}`}>
          <div className="dti-header">
            <h4>Debt-to-Income Ratio (DTI)</h4>
            <div className="dti-icon">{dtiRating.icon}</div>
          </div>

          <div className="dti-value">
            <span className="dti-percentage">{debtToIncomeRatio.toFixed(2)}%</span>
            <span className={`dti-rating ${dtiRating.color}`}>{dtiRating.label}</span>
          </div>

          <div className="dti-explanation">
            <p>
              Your debt-to-income ratio shows what percentage of your monthly income goes toward expenses and debt payments.
            </p>
            <ul className="dti-ranges">
              <li className="good">
                <strong>&lt; 20%:</strong> Excellent - Strong repayment capacity
              </li>
              <li className="fair">
                <strong>20-40%:</strong> Good to Fair - Moderate debt load
              </li>
              <li className="poor">
                <strong>&gt; 40%:</strong> High Risk - May affect loan approval
              </li>
            </ul>

            {debtToIncomeRatio > 50 && (
              <div className="warning-message">
                <AlertCircle size={18} />
                <p>
                  <strong>Warning:</strong> Your DTI ratio is high. Consider reducing expenses or increasing income before applying for a new loan.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="form-actions">
        <button type="button" onClick={onBack} className="btn btn-secondary">
          Back
        </button>
        <button type="submit" className="btn btn-primary">
          Continue to Bank Account Details
        </button>
      </div>
    </form>
  );
};
